import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { TileEmpty } from '../TileWrapper';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_MEMBERS = [
  { id: '1', name: 'John Smith', initials: 'JS', avatarUrl: null, role: 'Admin' },
  { id: '2', name: 'Sarah Johnson', initials: 'SJ', avatarUrl: null, role: 'Manager' },
  { id: '3', name: 'Mike Williams', initials: 'MW', avatarUrl: null, role: 'User' },
  { id: '4', name: 'Emma Brown', initials: 'EB', avatarUrl: null, role: 'User' },
  { id: '5', name: 'David Lee', initials: 'DL', avatarUrl: null, role: 'Manager' },
  { id: '6', name: 'Lisa Davis', initials: 'LD', avatarUrl: null, role: 'User' },
  { id: '7', name: 'James Wilson', initials: 'JW', avatarUrl: null, role: 'User' },
  { id: '8', name: 'Rachel Green', initials: 'RG', avatarUrl: null, role: 'User' },
  { id: '9', name: 'Tom Clark', initials: 'TC', avatarUrl: null, role: 'Admin' },
  { id: '10', name: 'Sophie Taylor', initials: 'ST', avatarUrl: null, role: 'User' },
  { id: '11', name: 'Oliver Martin', initials: 'OM', avatarUrl: null, role: 'Manager' },
  { id: '12', name: 'Emily White', initials: 'EW', avatarUrl: null, role: 'User' },
];

export function TeamDirectoryMiniTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ members: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      const limit = size === 'large' ? 12 : 8;
      setData({ members: MOCK_MEMBERS.slice(0, limit), total: 24 });
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const limit = size === 'large' ? 12 : 8;

        const { data: members, count, error } = await supabase
          .from('company_members')
          .select(`
            id,
            role,
            user_profiles!inner(id, first_name, last_name, avatar_url)
          `, { count: 'exact' })
          .eq('company_id', company.id)
          .limit(limit);

        if (error) throw error;

        const formatted = (members || []).map(m => ({
          id: m.user_profiles.id,
          name: `${m.user_profiles.first_name} ${m.user_profiles.last_name}`,
          initials: `${m.user_profiles.first_name?.[0] || ''}${m.user_profiles.last_name?.[0] || ''}`,
          avatarUrl: m.user_profiles.avatar_url,
          role: m.role,
        }));

        setData({ members: formatted, total: count || 0 });
      } catch (err) {
        console.error('Failed to load team directory:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, size, useMockData]);

  if (loading) {
    return (
      <div className="flex flex-wrap gap-2 justify-center">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.members.length === 0) {
    return <TileEmpty message="No team members" icon={Users} />;
  }

  const isLarge = size === 'large';
  const remaining = data.total - data.members.length;

  return (
    <div className="h-full flex flex-col">
      {/* Avatar Grid */}
      <div className={cn(
        "flex-1 flex flex-wrap gap-2 content-start",
        isLarge ? "justify-start" : "justify-center"
      )}>
        {data.members.map((member) => (
          <div
            key={member.id}
            onClick={() => navigate(`/profile/${member.id}`)}
            className="group relative cursor-pointer"
          >
            <Avatar className={cn(
              "transition-transform group-hover:scale-110",
              isLarge ? "h-12 w-12" : "h-10 w-10"
            )}>
              <AvatarImage src={member.avatarUrl} />
              <AvatarFallback className="text-xs font-medium bg-blue-100 text-blue-700">
                {member.initials}
              </AvatarFallback>
            </Avatar>

            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
              {member.name}
            </div>
          </div>
        ))}

        {remaining > 0 && (
          <div
            onClick={() => navigate('/company-team')}
            className={cn(
              "rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium cursor-pointer hover:bg-gray-300 transition-colors",
              isLarge ? "h-12 w-12 text-sm" : "h-10 w-10 text-xs"
            )}
          >
            +{remaining}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 border-t flex items-center justify-between text-sm">
        <span className="text-gray-600">{data.total} members</span>
        <button
          onClick={() => navigate('/company-team')}
          className="text-blue-600 hover:text-blue-700"
        >
          View All
        </button>
      </div>
    </div>
  );
}
